import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import i18n from "../../i18n/i18n";
const BASE_URL = process.env.REACT_APP_BASE_URL;

// Fetch all quotation request suppliers
export const fetchQuotationRequestSuppliers = createAsyncThunk(
  "quotationRequestSuppliers/fetchAll",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/quotations-request-suppliers${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      throw new Error(message);
    }
  }
);

// Fetch single quotation request supplier
export const fetchQuotationRequestSupplier = createAsyncThunk(
  "quotationRequestSuppliers/fetchOne",
  async (id) => {
    try {
      const response = await axios.get(`${BASE_URL}/quotations-request-suppliers/${id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load data";
      throw new Error(message);
    }
  }
);

// Add quotation request supplier
export const addQuotationRequestSupplier = createAsyncThunk(
  "quotationRequestSuppliers/add",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/quotations-request-suppliers`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to add data";
      return rejectWithValue({ message });
    }
  }
);

// Update quotation request supplier
export const updateQuotationRequestSupplier = createAsyncThunk(
  "quotationRequestSuppliers/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const response = await axios.post(
        `${BASE_URL}/quotations-request-suppliers/update/${id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      if (response.data?.status === false) {
        return rejectWithValue(
          typeof response.data === "object" && response.data !== null
            ? response.data
            : { message: "Failed to update data" }
        );
      }
      return response.data;
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue(
        typeof data === "object" && data !== null
          ? data
          : { message: "Failed to update data" }
      );
    }
  }
);

// Update quotation request supplier status
export const updateQuotationRequestSupplierStatus = createAsyncThunk(
  "quotationRequestSuppliers/updateStatus",
  async ({ id, status }, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("status", status);

      const response = await axios.post(
        `${BASE_URL}/quotations-request-suppliers/${id}/status`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "multipart/form-data",
            Lang: i18n.language,
          },
        }
      );
      if (response.data?.status === false) {
        return rejectWithValue(
          typeof response.data === "object" && response.data !== null
            ? response.data
            : { message: "Failed to update status" }
        );
      }
      return { id, status, message: response.data?.message };
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue(
        typeof data === "object" && data !== null
          ? data
          : { message: "Failed to update status" }
      );
    }
  }
);

// Fetch service types (quotations-request-service-types)
export const fetchQuotationRequestServiceTypes = createAsyncThunk(
  "quotationRequestSuppliers/fetchQuotationRequestServiceTypes",
  async () => {
    try {
      const response = await axios.get(`${BASE_URL}/quotations-request-service-types`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load service types";
      throw new Error(message);
    }
  }
);

// Fetch service types from /api/service-types (for filtering)
export const fetchServiceTypes = createAsyncThunk(
  "quotationRequestSuppliers/fetchServiceTypes",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/service-types${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load service types";
      throw new Error(message);
    }
  }
);

// Fetch transport types
export const fetchTransportTypes = createAsyncThunk(
  "quotationRequestSuppliers/fetchTransportTypes",
  async () => {
    try {
      const response = await axios.get(`${BASE_URL}/transport-types`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load transport types";
      throw new Error(message);
    }
  }
);

// Fetch clients
export const fetchClients = createAsyncThunk(
  "quotationRequestSuppliers/fetchClients",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/clients${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load clients";
      throw new Error(message);
    }
  }
);

// Fetch cities
export const fetchCities = createAsyncThunk(
  "quotationRequestSuppliers/fetchCities",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/cities${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load cities";
      throw new Error(message);
    }
  }
);

// Fetch quotation requests
export const fetchQuotationRequests = createAsyncThunk(
  "quotationRequestSuppliers/fetchQuotationRequests",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/quotations-requests${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load quotation requests";
      throw new Error(message);
    }
  }
);

// Fetch quotation request customers
export const fetchQuotationRequestCustomers = createAsyncThunk(
  "quotationRequestSuppliers/fetchQuotationRequestCustomers",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/quotations-request-customers${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load quotation request customers";
      throw new Error(message);
    }
  }
);

// Fetch winch partners
export const fetchWinchPartners = createAsyncThunk(
  "quotationRequestSuppliers/fetchWinchPartners",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/partners${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch partners";
      throw new Error(message);
    }
  }
);

// Fetch winch branches
export const fetchWinchBranches = createAsyncThunk(
  "quotationRequestSuppliers/fetchWinchBranches",
  async (params = {}) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/winch-branches${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data.data?.data || [];
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load winch branches";
      throw new Error(message);
    }
  }
);

// Fetch business sectors
export const fetchBusinessSectors = createAsyncThunk(
  "quotationRequestSuppliers/fetchBusinessSectors",
  async (params = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      Object.keys(params).forEach((key) => {
        if (params[key] !== "" && params[key] !== null && params[key] !== undefined) {
          queryParams.append(key, params[key]);
        }
      });
      const url = `${BASE_URL}/business-sectors${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
          Lang: i18n.language,
        },
      });
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || "Failed to load business sectors";
      return rejectWithValue({ message });
    }
  }
);

const QuotationRequestSuppliersSlice = createSlice({
  name: "quotationRequestSuppliers",
  initialState: {
    data: [],
    pagination: null,
    record: null,
    serviceTypes: [],
    serviceTypesForFilter: [], // Service types from /api/service-types
    transportTypes: [],
    clients: [],
    winchPartners: [],
    winchBranches: [],
    cities: [],
    quotationRequests: [],
    quotationRequestCustomers: [],
    isLoading: false,
    error: null,
    success: null,
  },
  reducers: {
    clearState: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all
      .addCase(fetchQuotationRequestSuppliers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationRequestSuppliers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data || [];
        state.pagination = action.payload.pagination || null;
      })
      .addCase(fetchQuotationRequestSuppliers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      // Fetch one
      .addCase(fetchQuotationRequestSupplier.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationRequestSupplier.fulfilled, (state, action) => {
        state.isLoading = false;
        state.record = action.payload;
      })
      .addCase(fetchQuotationRequestSupplier.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load data";
      })
      // Add
      .addCase(addQuotationRequestSupplier.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(addQuotationRequestSupplier.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload.message;
      })
      .addCase(addQuotationRequestSupplier.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to add data";
      })
      // Update
      .addCase(updateQuotationRequestSupplier.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateQuotationRequestSupplier.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Updated successfully";
      })
      .addCase(updateQuotationRequestSupplier.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || action.error?.message || "Failed to update data";
      })
      // Update status
      .addCase(updateQuotationRequestSupplierStatus.pending, (state) => {
        state.isLoading = true;
        state.error = null;
        state.success = null;
      })
      .addCase(updateQuotationRequestSupplierStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.success = action.payload?.message || "Status updated successfully";
        const { id, status } = action.payload || {};
        if (id != null && Array.isArray(state.data)) {
          const row = state.data.find((item) => String(item.id) === String(id));
          if (row) row.status = status;
        }
      })
      .addCase(updateQuotationRequestSupplierStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.error =
          action.payload?.message || action.error?.message || "Failed to update status";
      })
      // Fetch service types
      .addCase(fetchQuotationRequestServiceTypes.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchQuotationRequestServiceTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.serviceTypes = action.payload || [];
      })
      .addCase(fetchQuotationRequestServiceTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load service types";
      })
      // Fetch clients
      .addCase(fetchClients.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchClients.fulfilled, (state, action) => {
        state.isLoading = false;
        state.clients = action.payload || [];
      })
      .addCase(fetchClients.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load clients";
      })
      // Fetch cities
      .addCase(fetchCities.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchCities.fulfilled, (state, action) => {
        state.isLoading = false;
        state.cities = action.payload || [];
      })
      .addCase(fetchCities.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load cities";
      })
      // Fetch quotation requests
      .addCase(fetchQuotationRequests.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchQuotationRequests.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotationRequests = action.payload || [];
      })
      .addCase(fetchQuotationRequests.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load quotation requests";
      })
      // Fetch quotation request customers
      .addCase(fetchQuotationRequestCustomers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchQuotationRequestCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.quotationRequestCustomers = action.payload || [];
      })
      .addCase(fetchQuotationRequestCustomers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load quotation request customers";
      })
      // Fetch winch partners
      .addCase(fetchWinchPartners.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWinchPartners.fulfilled, (state, action) => {
        state.isLoading = false;
        state.winchPartners = action.payload || [];
      })
      .addCase(fetchWinchPartners.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load winch partners";
      })
      // Fetch winch branches
      .addCase(fetchWinchBranches.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchWinchBranches.fulfilled, (state, action) => {
        state.isLoading = false;
        state.winchBranches = action.payload || [];
      })
      .addCase(fetchWinchBranches.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load winch branches";
      })
      // Fetch business sectors
      .addCase(fetchBusinessSectors.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchBusinessSectors.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.data?.pagination?.current_page === 1) {
          state.businessSectors = action.payload?.data?.data || [];
        } else {
          state.businessSectors = [...(state.businessSectors || []), ...(action.payload?.data?.data || [])];
        }
        state.businessSectorsPagination = action.payload?.data?.pagination || null;
      })
      .addCase(fetchBusinessSectors.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload?.message || "Failed to load business sectors";
      })
      // Fetch service types (from /api/service-types)
      .addCase(fetchServiceTypes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchServiceTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.serviceTypesForFilter = action.payload || [];
      })
      .addCase(fetchServiceTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load service types";
      })
      // Fetch transport types
      .addCase(fetchTransportTypes.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchTransportTypes.fulfilled, (state, action) => {
        state.isLoading = false;
        state.transportTypes = action.payload || [];
      })
      .addCase(fetchTransportTypes.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error?.message || "Failed to load transport types";
      });
  },
});

export const { clearState } = QuotationRequestSuppliersSlice.actions;
export default QuotationRequestSuppliersSlice.reducer;

